import { defineStore } from "pinia";
import db from "@/db";
import crudGenerator from "@/api/utils/offlineSync";
import { constatRestClient } from "@/api";
import { useProjectsStore } from "@/store/modules/projects";

const { innerFetch, innerCreate, innerUpdate, innerDelete } = crudGenerator(
  db.models.Constat
);

const CATALOG_SYNC_TTL_MS = 15 * 60 * 1000;

let catalogSync = null;
let catalogSyncedAt = 0;
let isCatalogSyncInFlight = false;

export const useConstatsStore = defineStore("constats", {
  state: () => ({
    constats: [],
    constatIsLoading: false,
    detectionsInFlight: 0,
  }),
  actions: {
    async fetchConstats(filters = {}) {
      // G.R 03/08/26 ATMO-567 : flag capturé AVANT le fetch (listAll mute filters) ; un
      // fetch catalogue FILTRÉ ne doit jamais déclencher la purge ci-dessous
      const isFullCatalogFetch =
        filters.projectIsnull && Object.keys(filters).length === 1;
      const res = await innerFetch(
        filters,
        useProjectsStore().isOfflineState,
        (isLoading) => {
          this.constatIsLoading = isLoading;
        }
      );
      if (isFullCatalogFetch && res?.success) {
        const fetchedUuids = new Set(res.results.map(({ uuid }) => uuid));
        const staleUuids = (await db.constats.toArray())
          .filter(
            (constat) => !constat.project && !fetchedUuids.has(constat.uuid)
          )
          .map((constat) => constat.uuid);
        if (staleUuids.length) await db.constats.bulkDelete(staleUuids);
      }
      return res;
    },

    syncCatalog({ force = false } = {}) {
      const isFresh = Date.now() - catalogSyncedAt < CATALOG_SYNC_TTL_MS;
      if (catalogSync && (isCatalogSyncInFlight || (!force && isFresh)))
        return catalogSync;
      isCatalogSyncInFlight = true;
      catalogSync = this.fetchConstats({ projectIsnull: true })
        .then((res) => {
          if (res?.success) catalogSyncedAt = Date.now();
          else catalogSync = null;
          return res;
        })
        .catch((error) => {
          catalogSync = null;
          throw error;
        })
        .finally(() => {
          isCatalogSyncInFlight = false;
        });
      return catalogSync;
    },

    async ensureConstatsInCatalog(constatUuids) {
      const uuids = [...new Set(constatUuids)];
      if (!uuids.length) return;
      const foundCount = await db.constats.where("uuid").anyOf(uuids).count();
      if (foundCount < uuids.length) await this.syncCatalog({ force: true });
    },

    async filterConstat({ query }) {
      const { normeName, constatWording } = query;
      this.constatIsLoading = true;

      // G.R 03/08/26 ATMO-567 : le N° (uniqueKey, ex. 117-1-2) est l'identifiant usuel
      // des écarts — chaque champ de recherche matche aussi dessus ; comparaison
      // null-safe (champ absent sur certains constats custom).
      const contains = (value, search) =>
        (value || "").toLowerCase().includes((search || "").toLowerCase());
      try {
        this.constats = await db.constats
          .filter(
            (item) =>
              (contains(item.normeName, normeName) ||
                contains(item.uniqueKey, normeName)) &&
              (contains(item.wording, constatWording) ||
                contains(item.uniqueKey, constatWording))
          )
          .toArray();
      } finally {
        this.constatIsLoading = false;
      }
    },

    async updateConstat(form) {
      this.constatIsLoading = true;
      try {
        const constat = await innerUpdate(
          form,
          useProjectsStore().isOfflineState
        );
        this.replaceConstat(constat);
        return constat;
      } finally {
        this.constatIsLoading = false;
      }
    },

    async deleteConstat(constatUuid) {
      const projectsStore = useProjectsStore();
      this.constatIsLoading = true;
      try {
        await innerDelete(
          constatUuid,
          projectsStore.isOfflineState,
          projectsStore.currentProjectUuid
        );
        this.constats = this.constats.filter(
          ({ uuid }) => uuid !== constatUuid
        );
      } finally {
        this.constatIsLoading = false;
      }
    },

    async createConstat(form) {
      this.constatIsLoading = true;
      try {
        const constat = await innerCreate(
          form,
          useProjectsStore().isOfflineState
        );
        this.constats.push(constat);
        return constat;
      } finally {
        this.constatIsLoading = false;
      }
    },

    async setAllConstats() {
      this.constatIsLoading = true;
      try {
        this.constats = await db.constats.toArray();
      } finally {
        this.constatIsLoading = false;
      }
    },

    async detectConstatsByInstallation(form) {
      this.detectionsInFlight += 1;
      try {
        return await constatRestClient.detectionByInstallation(form);
      } finally {
        this.detectionsInFlight -= 1;
      }
    },

    replaceConstat(constat) {
      const index = this.constats.findIndex(
        ({ uuid }) => constat.uuid === uuid
      );

      if (index < 0) return;

      this.constats[index] = constat;
      this.constats = [...this.constats];
    },
  },
});
